import { Datos } from './formulario-dinamico.model';
import { Actividad } from './actividad.model';
import { Usuario } from './usuario.model';
import { FormConfig, DynamicComponentForm } from './forms.model';
import { Documento, DocumentoAsociado } from './documento.model';
import { Static } from './../util/static';
import { merge } from 'lodash';

export type NameTaskType = 'none' | 'condition' | 'microProcess' | 'assignment' | 'form' | 'secondary';

/**
 * Clase basic
 *
 * @export
 * @class TareaBasic
 */
export class TareaBasic {
  id: number;
  nombre: string;
  descripcion: string;
}

/**
 * Complementa al módelo de "Tareas".
 *
 * @export
 * @class TareaAsociacion
 */
export class TareaAsociacion {
  activo: boolean;
  descripcion: string;
  documentos: Array<Documento>;
  fecha_registro: Date;
  id_tarea_inicio?: number;
  id_tarea_destino: number;
  nombre: string;
  orden: number;
  rechazable: boolean;
  responsables: Array<Usuario>;
  tarea_actividad: number;
  tarea_condicion: Array<TareaCondicion>;
  tarea_estado: TareaEstado;
  tarea_rol: Array<TareaRol>;
  tarea_tipo: TareaTipo;
  tipo_asociacion: string;
  id_tipo_asociacion: number;
  alias: string;
  datos: Array<Datos>;
  documentos_asociados: Array<DocumentoAsociado>;
  proceso_externo: boolean;
}

/**
 * Aplica para las tareas de tipo condición y complementa
 * al módelo de "Tareas".
 *
 * @export
 * @class TareaCondicion
 */
export class TareaCondicion {
  id_opcion: number;
  tarea_condicion: string;
  opcion: string;
  mascara: string;
  alias: string;
  orden: number;
  seleccion: boolean;
}

/**
 * Opción usada dentro de una condición en la administración.
 *
 * @export
 * @class TareaOpcionAdmin
 */
export class TareaOpcionAdmin {
  opcion: string;
  mascara: string;
  orden: number;
  id_opcion: number;
}

/**
 * Condición usada dentro de la administración.
 *
 * @export
 * @class TareaCondicionAdmin
 */
export class TareaCondicionAdmin {
  condicion: string;
  alias: string;
  opciones: TareaOpcionAdmin[];
}

/**
 * Son todos los posibles estados que puede tener una tarea.
 * Este complementa el módelo de "Tareas".
 *
 * @export
 * @class TareaEstado
 */
export class TareaEstado extends TareaBasic { }

/**
 * Son todos los posibles tipos de finalización para una tarea.
 *
 * Los endpoints que satisface son: typeCompletion
 *
 * @export
 * @class TareaTypeCompletion
 * @extends {TareaBasic}
 */
export class TareaTipoFinalizacion extends TareaBasic { }

/**
 * Configuración de las finalizaciones de una tarea.
 *
 * @export
 * @class TareaFinalizacion
 */
export class TareaFinalizacion {
  tarea_inicio: number;
  tarea_destino: number;
  parametros: Partial<{
    finalizacion: boolean;
    enviar_asignacion: boolean;
  }>;
  tipo_finalizacion: number;
  opcion: number | null;

  constructor(values: Partial<TareaFinalizacion>) {
    Static.setAll(
      this,
      merge(
        {
          parametros: {
            finalizacion: true,
          },
          opcion: null
        },
        values
      )
    );
  }
}

/**
 * Son los roles que pueden realizar una acción sobre la tarea.
 * Estos roles se categorizan en los que asignan y/o son responsables.
 * Este módelo complementa el de "Tarea".
 *
 * @export
 * @class TareaRol
 */
export class TareaRol {
  rol_id: number;
  nombre: string;
  descripcion: string;
  asignacion: boolean;
  responsable: boolean;

  constructor(values: Partial<TareaRol>) {
    Static.setAll(
      this,
      merge(
        {
          asignacion: false,
          responsable: false
        },
        values
      )
    );
  }
}

/**
 * Son los posibles tipos que se pueden asignar a una tarea, estos
 * definen el comportamiento de una tarea.
 * Este módelo complementa el de "Tarea".
 *
 * @export
 * @class TareaTipo
 */
export class TareaTipo {
  id: number;
  nombre: string;
  descripcion: string;
}

/**
 * Tarea de una actividad que pertenece a un trámite por default
 * o instancia.
 *
 * Los endpoints que satisface son:
 * instanceDefaultTramite, microprocesInstance, instanceTramite.
 *
 * @export
 * @class Tarea
 */
export class TareaDefault {
  id: number;
  nombre: string;
  descripcion: string;
  orden: number;
  fecha_registro: Date;
  rechazable: boolean;
  rechazo?: Rechazo;
  tarea_tipo: TareaTipo;
  tarea_rol: TareaRol[];
  tarea_estado: TareaEstado;
  tarea_actividad: number;
  tarea_asociacion: TareaAsociacion[];
  tarea_condicion: TareaCondicion[] | TareaCondicionAdmin[];
  tarea_finalizacion: TareaFinalizacion[];
}

/**
 * Información extra de una tarea.
 *
 * @export
 * @class TareaMetaData
 */
export class TareaMetaData {
  // Id de la actividad a la que pertenece la tarea
  idActivity: number;
  // Indice de la tarea dentro del array de tareas.
  index: number;
  // Indice de la última tarea dentro del array de tareas.
  indexLast: number;
  // Define si esta tarea es la última dentro del array de tareas.
  isLast: boolean;
  // Indice de la última tarea activa dentro del array de tareas.
  lastIndexActive: number;
  // Define si esta tarea es la última activa dentro del array de tareas.
  isLastActive: boolean;
  // Índice que se muestra al usuario en el flujo.
  indexFlow: number;
  // Define si la tarea pertenece a la última actividad activa.
  belongsToLastActivity: boolean;
  // Define el tipo de formulario de la tarea.
  formType: FormConfig<DynamicComponentForm>;
}

/**
 * Configuración para finalizar una tarea usando el endpoint 'instanceMethods'
 *
 * @export
 * @interface ConfigFinishDefault
 */
export interface ConfigFinishDefault {
  tarea: number;
  id_opcion?: number;
  id_opciones?: number[];
  tramite_asociacion?: number;
}

/**
 * Es la configuración de una forma especifica dentro de una tarea.
 *
 * @export
 * @class TareaFormaEspecifica
 */
export class TareaForma {
  id_forma: number;
  forma_especifica: boolean;
  nombre: string;
  parametros: any;
  estilo: any;
  version: number;
  orden: number;
  tabla: boolean;
  documento: boolean;
  condicion: boolean;
  lista_chequeo: boolean;
  opciones: any;
}

export class Tarea extends TareaDefault {
  activo: boolean;
  fecha_activacion: Date;
  fecha_finalizacion: Date;
  tarea_forma: Array<TareaForma>;
  responsables: Usuario[];
  meta?: Partial<TareaMetaData>;
  documentos?: Array<Documento>;
  datos?: Array<Datos>;
  tabla_datos?: any;
  historial_microproceso?: Array<HistorialMicroproceso>;

  constructor(values: Partial<Tarea>) {
    super();
    Static.setAll(
      this,
      merge(
        {
          activo: true,
          orden: 1,
          rechazable: false,
          tarea_asociacion: [],
          tarea_condicion: [],
          tarea_finalizacion: [],
          documentos: [],
          tarea_forma: [],
        },
        values
      )
    );
  }
}

/**
 * Es un tipo especifico de tarea, se diferencia a una tarea normal
 * ya que esta posee actividades.
 *
 * Los endpoints que satisface son: microprocesInstance.
 *
 * @export
 * @class Microproceso
 */
export class Microproceso {
  id: number;
  descripcion: string;
  tarea_estado: TareaEstado;
  orden: number;
  actividades: Actividad[];
  titulos?: any;

  constructor(values: Partial<Microproceso>) {
    Static.setAll(
      this,
      merge(
        {
          actividades: []
        },
        values
      )
    );
  }
}

/**
 * Modelo de la consulta de tipos de asociasiones endpoint typeAssociation
 *
 * @export
 * @class TipoAsociaciones
 */
export class TipoAsociacion {
  id: number;
  nombre: string;
  descripcion: string;
}

/**
 * Modelo de la tarea de asociasion para la predeterminada en la administracion
 *
 * @export
 * @class TareaAsociacionPred
 */
export class TareaAsociacionPred {
  id_tarea_inicio: number;
  id_tarea_destino: number;
  id_tipo_asociacion: number;
  tipo_asociacion: string;
  task: any;
  alias: string;
  documentos: Array<DocumentoAsociado>;
  proceso_externo: boolean;
}

/**
 * Modelo del endpoint taskTramiteDefault que trae todas las tareas de un tramite
 *
 * @export
 * @class TareaTramite
 */
export class TareaTramite {
  id: number;
  nombre: string;
}

/**
 * Modelo del rechazo de una asignacion
 *
 * @export
 * @class Rechazo
 */
export class Rechazo {
  descripcion: string;
  en_historial: boolean;
  fecha_rechazo: string;
  finalizado: boolean;
  responsable: UsuarioAssignment;
}

/**
 * Modelo principal de la consulta de histolrial de asignaciones
 *
 * @export
 * @class Asignacion
 */
export class Asignacion {
  id: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
  orden: number;
  fecha_registro: string;
  rechazable: boolean;
  responsables: Array<UsuarioAssignment>;
  tarea_tipo: TareaTipo;
  tarea_rol: TareaRol;
  tarea_estado: TareaEstado;
  historial: HistorialRechazo[];
}

/**
 * Modelo del rechazo de asignaciones
 *
 * @export
 * @class HistorialRechazo
 */
export class HistorialRechazo {
  fecha_registro: string;
  fecha_finalizacion: string;
  responsables: Array<UsuarioAssignment>;
  version: number;
  usuarios_asignados: Array<UsuarioAssignment>;
  rechazo: Rechazo;
}

/**
 * Modelo de los usuarios en las asignaciones
 *
 * @export
 * @class UsuarioAssignment
 */
export class UsuarioAssignment {
  nombres: string;
  apellidos: string;
}

/**
 * Modelo de un registro de historial de un microproceso
 *
 * @export
 * @class HistorialMicroproceso
 */
export class HistorialMicroproceso {
  descripcion: string;
  finalizado: boolean;
  id: number;
  orden: number;
}

/**
 * Modelo de una tarea basica sin mucha informacion
 * Modelo complementario de la consulta de tramite basico
 *
 * @export
 * @class TareaBasica
 */
export class TareaBasica {
  id: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
  orden: number;
  fecha_registro: Date;
  rechazable: boolean;
  tarea_tipo: TareaTipo;
  tarea_estado: TareaEstado;
  tarea_actividad: number;
  tarea_forma: Array<TareaForma>;
}

/**
 * Modelo de disparadores de tareas
 *
 * @export
 * @class Disparador
 */
export class Disparador {
  id: number;
  nombre: string;
  descripcion: string;
  ruta: string;
}



// WEBPACK FOOTER //
// ./src/app/shared/models/tarea.model.ts